import { geohashQueryBounds } from 'geofire-common';

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

export interface LatLngBounds {
  northeast: LatLngLiteral;
  southwest: LatLngLiteral;
}

const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;

const distanceBetweenKm = (
  location1: [number, number],
  location2: [number, number],
) => {
  const radius = 6371;
  const latDelta = degreesToRadians(location2[0] - location1[0]);
  const lonDelta = degreesToRadians(location2[1] - location1[1]);
  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(degreesToRadians(location1[0])) *
      Math.cos(degreesToRadians(location2[0])) *
      Math.sin(lonDelta / 2) *
      Math.sin(lonDelta / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radius * c;
};

const getCenterOfBounds = (b: LatLngBounds): LatLngLiteral => ({
  lat: (b.southwest.lat + b.northeast.lat) / 2,
  lng: (b.southwest.lng + b.northeast.lng) / 2,
});

const getRadiusOfBoundsInM = (bounds: LatLngBounds): number => {
  const circumferenceInKM = distanceBetweenKm(
    [bounds.northeast.lat, bounds.northeast.lng],
    [bounds.southwest.lat, bounds.southwest.lng],
  );
  const circumferenceInM = circumferenceInKM * 1000;
  return circumferenceInM / 2;
};

const longestCommonPrefix = (strings: string[]): string => {
  let prefix = '';
  for (let i = 0; i < strings[0].length; i++) {
    for (let j = 1; j < strings.length; j++) {
      if (strings[j][i] !== strings[0][i]) {
        return prefix;
      }
    }
    prefix = prefix + strings[0][i];
  }
  return prefix;
};

/** Calculates a geohash to fully contain a given area. */
export const getGeohashBounds = (bounds: LatLngBounds): string => {
  const center: LatLngLiteral = getCenterOfBounds(bounds);
  const radius = getRadiusOfBoundsInM(bounds);
  const hashBounds: string[][] = geohashQueryBounds(
    [center.lat, center.lng],
    radius,
  );
  const flat = hashBounds.reduce(
    (acc: string[], val: string[]) => [...acc, ...val],
    [],
  );
  return longestCommonPrefix(flat);
};
